/* eslint-disable react-refresh/only-export-components -- vendored shadcn/ui component */
import { type BaseUIEvent, Combobox as ComboboxPrimitive } from '@base-ui/react';
import { CheckIcon, ChevronsUpDownIcon, LoaderCircleIcon, PackageOpenIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Checkbox } from '@/_shared/components/ui/checkbox';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/_shared/components/ui/input-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { cn } from '@/_shared/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
  /**
   * Extra text the search matches in ADDITION to `label` — e.g. a timezone's IANA id / region so it's
   * findable by more than its display label. When no option carries `keywords`, Base UI's default
   * (label-only, collator-based) filtering is used unchanged.
   */
  keywords?: string;
  /** Disable this option so it can't be toggled (base-ui handles the interaction + greyed styling). */
  disabled?: boolean;
  /** Hover tooltip (rendered via ui/tooltip) — e.g. why a disabled option is locked. */
  tooltip?: React.ReactNode;
  /** Optional leading icon rendered before the label in the option row (e.g. a type glyph). */
  icon?: React.ReactNode;
  /** Optional trailing content, right-aligned in the option row + shown after the label on the popup
   *  trigger (e.g. a source/table hint). */
  meta?: React.ReactNode;
}

/** A titled section of options — rendered as a labeled group; empty groups auto-hide while filtering. */
export interface ComboboxOptionGroup {
  title: string;
  options: ComboboxOption[];
}

/** The `options` prop: EITHER a flat option list OR a list of titled groups — never interleaved. */
export type ComboboxItems = readonly ComboboxOption[] | readonly ComboboxOptionGroup[];

type ComboboxItem = ComboboxOption | ComboboxOptionGroup;

function isOptionGroup(o: ComboboxItem): o is ComboboxOptionGroup {
  return 'options' in o;
}

// The array-level union above can't be `.map`/`.some`/`.flatMap`'d directly (TS rejects calling a
// method on a union of array types), so widen to this element view before iterating. Callers still
// cannot CONSTRUCT a mixed array — tightening the public type is the point.
function asItems(items: ComboboxItems): readonly ComboboxItem[] {
  return items;
}

/** All leaf options, flattening any groups — used to resolve the selected value(s). */
function flattenComboboxOptions(items: ComboboxItems): ComboboxOption[] {
  return asItems(items).flatMap((o) => (isOptionGroup(o) ? o.options : [o]));
}

/**
 * Base UI `filter` that matches `label` OR `keywords`, or `undefined` to keep Base UI's default
 * (label-only, collator-based) filtering when no option carries keywords — so consumers without
 * keywords are byte-identical to before this existed. Base UI applies the filter to leaf options
 * (within groups too), so `item` is always a {@link ComboboxOption}.
 */
function keywordsFilter(flat: ComboboxOption[]): ((item: unknown, query: string) => boolean) | undefined {
  if (!flat.some((o) => o.keywords)) return undefined;
  return (item, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const o = item as ComboboxOption;
    return o.label.toLowerCase().includes(q) || (o.keywords?.toLowerCase().includes(q) ?? false);
  };
}

/**
 * Base UI `items`: the flat list as-is, or a `{ value: title, items }[]` group array when any group is
 * present (a bare option becomes a title-less group). Base UI filters within groups and hides empties.
 */
function toBaseUIItems(items: ComboboxItems): { baseItems: readonly unknown[]; grouped: boolean } {
  const list = asItems(items);
  if (!list.some(isOptionGroup)) return { baseItems: list, grouped: false };
  return {
    baseItems: list.map((o) => (isOptionGroup(o) ? { value: o.title, items: o.options } : { value: '', items: [o] })),
    grouped: true,
  };
}

/** ComboboxList children: a flat render fn, or a grouped renderer (Group → GroupLabel + Collection). */
function comboboxListChildren(
  grouped: boolean,
  renderItem: (item: ComboboxOption) => React.ReactElement,
): React.ReactNode | ((item: unknown, index: number) => React.ReactNode) {
  if (!grouped) return renderItem as unknown as (item: unknown, index: number) => React.ReactNode;
  return (groupItem: unknown, index: number) => {
    const group = groupItem as { value?: string; items: ComboboxOption[] };
    return (
      <ComboboxGroup key={index} items={group.items}>
        {group.value ? <ComboboxLabel>{group.value}</ComboboxLabel> : null}
        <ComboboxCollection>{renderItem}</ComboboxCollection>
      </ComboboxGroup>
    );
  };
}

/** Field height — `md` = 28px, `lg` = 32px (same as Select / Input). */
export type ComboboxSize = 'md' | 'lg';

/** `input` = always-visible search field; `popup` = button trigger, search inside the popover. */
export type ComboboxVariant = 'input' | 'popup';

const DEFAULT_EMPTY_MESSAGE = 'No items found';

/** Exact height when empty; `min-*` keeps that floor when chips wrap. */
const SIZE_CHIPS_HEIGHT = { md: 'h-7 min-h-7', lg: 'h-8 min-h-8' } as const;

/**
 * Chip / chip-input height (multi-select with chips), paired with the field's `py-px` so a single row lands
 * exactly on the size floor (md=28 / lg=32): md 24 + 2pad + 2border = 28; lg 28 + 2 + 2 = 32.
 */
const SIZE_CHIP_ITEM = {
  md: '[&_[data-slot=combobox-chip]]:h-6 [&_[data-slot=combobox-chip-input]]:h-6',
  lg: '[&_[data-slot=combobox-chip]]:h-7 [&_[data-slot=combobox-chip-input]]:h-7',
} as const;

const SIZE_HEIGHT = { md: 'h-7', lg: 'h-8' } as const;

const TRIGGER_BASE_CLASS =
  'w-fit min-w-0 rounded-md border border-grey-75 bg-grey-0 shadow-none transition-[color,box-shadow,border-color] hover:border-grey-200 hover:shadow-secondary has-[[data-slot=input-group-control]:focus-visible]:border-primary has-[[data-slot=input-group-control]:focus-visible]:shadow-primary has-disabled:cursor-not-allowed has-disabled:border-transparent has-disabled:bg-grey-25 has-disabled:text-grey-400 has-disabled:hover:border-transparent has-disabled:hover:shadow-none focus-within:border-primary focus-within:shadow-primary focus-within:hover:border-primary focus-within:hover:shadow-primary aria-invalid:border-destructive aria-invalid:hover:border-destructive aria-invalid:hover:shadow-destructive aria-invalid:focus-within:border-destructive aria-invalid:focus-within:shadow-destructive aria-invalid:focus-within:hover:shadow-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:border-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:hover:border-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:hover:shadow-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:focus-within:border-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:focus-within:shadow-destructive has-[[data-slot=input-group-control][aria-invalid=true]]:focus-within:hover:shadow-destructive';

/** Select-like button chrome for the popup-variant trigger. */
const POPUP_TRIGGER_CLASS =
  'flex w-fit min-w-0 items-center justify-between gap-2 rounded-md border border-grey-75 bg-grey-0 py-1 pr-1 pl-2 text-sm font-medium whitespace-nowrap text-grey-800 transition-[color,box-shadow,border-color] outline-none hover:border-grey-200 hover:shadow-secondary focus-visible:border-primary focus-visible:shadow-primary data-popup-open:border-primary data-popup-open:shadow-primary data-popup-open:hover:border-primary data-popup-open:hover:shadow-primary data-placeholder:font-normal data-placeholder:text-grey-300 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-grey-25 disabled:text-grey-400 disabled:hover:border-transparent disabled:hover:shadow-none aria-invalid:border-destructive aria-invalid:hover:border-destructive aria-invalid:hover:shadow-destructive aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:shadow-destructive aria-invalid:data-popup-open:border-destructive aria-invalid:data-popup-open:shadow-destructive aria-invalid:data-popup-open:hover:shadow-destructive';

const POPOVER_CONTENT_CLASS =
  'group/combobox-content relative max-h-96 min-w-(--anchor-width) w-max max-w-[max(var(--anchor-width),22.5rem)] origin-(--transform-origin) overflow-hidden rounded-xl border border-grey-100 bg-popover text-popover-foreground shadow-md duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95';

const OPTION_CLASS =
  'relative flex h-8 w-full min-w-0 cursor-default items-center gap-2 rounded-md px-2 text-sm font-normal text-grey-800 outline-hidden select-none data-highlighted:bg-grey-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0';

const Combobox = ComboboxPrimitive.Root;

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <ChevronsUpDownIcon data-slot="combobox-trigger-icon" className="pointer-events-none size-4 text-grey-500" />
    </ComboboxPrimitive.Trigger>
  );
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      render={<InputGroupButton variant="tertiary" size="icon-xs" />}
      className={cn(className)}
      {...props}
    >
      <XIcon className="pointer-events-none" />
    </ComboboxPrimitive.Clear>
  );
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  loading = false,
  showTrigger = true,
  showClear = false,
  size = 'md',
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  showTrigger?: boolean;
  showClear?: boolean;
  loading?: boolean;
  /** Field height — `md` = 28px, `lg` = 32px */
  size?: ComboboxSize;
}) {
  const isDisabled = disabled || loading;

  // Popup variant: search lives inside the popover without InputGroup chrome/trigger.
  if (!showTrigger && !showClear) {
    return (
      <ComboboxPrimitive.Input
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={cn(
          'h-7 w-full min-w-0 rounded-md bg-transparent px-2 text-sm font-medium text-grey-800 outline-none',
          'placeholder:font-normal placeholder:text-grey-300',
          'disabled:cursor-not-allowed disabled:text-grey-400',
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <InputGroup
      size={size}
      className={cn(TRIGGER_BASE_CLASS, 'hover:shadow-secondary', className)}
      data-disabled={isDisabled || undefined}
    >
      <ComboboxPrimitive.Input
        render={<InputGroupInput disabled={isDisabled} />}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      />
      <InputGroupAddon align="inline-end" className="pr-1">
        {loading ? (
          <LoaderCircleIcon
            className="pointer-events-none size-4 shrink-0 animate-spin text-grey-500"
            aria-hidden="true"
          />
        ) : (
          showTrigger && (
            <InputGroupButton
              size="icon-xs"
              variant="tertiary"
              asChild
              data-slot="input-group-button"
              className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
              disabled={isDisabled}
            >
              <ComboboxTrigger />
            </InputGroupButton>
          )
        )}
        {showClear && !loading && <ComboboxClear disabled={isDisabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
}

function ComboboxContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  alignOffset = 0,
  anchor,
  container,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'> & {
    /** Portal target — use a modal/sheet content node so the list stays clickable under Radix `pointer-events: none` on `body`. */
    container?: React.ComponentProps<typeof ComboboxPrimitive.Portal>['container'];
  }) {
  return (
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(POPOVER_CONTENT_CLASS, className)}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn('max-h-60 scroll-py-1 overflow-x-hidden overflow-y-auto p-1.5 data-empty:p-0', className)}
      {...props}
    />
  );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item data-slot="combobox-item" className={cn(OPTION_CLASS, 'pr-8', className)} {...props}>
      {children}
      <ComboboxPrimitive.ItemIndicator
        data-slot="combobox-item-indicator"
        render={<span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />}
      >
        <CheckIcon className="pointer-events-none size-4 text-grey-800" strokeWidth={2} />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />;
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn('px-2 py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
}

function ComboboxEmpty({ className, children = DEFAULT_EMPTY_MESSAGE, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        'hidden w-full flex-col items-center justify-center group-data-empty/combobox-content:flex',
        className,
      )}
      {...props}
    >
      <Empty className="w-full min-w-0 flex-none items-center gap-0 border-0 p-4 md:p-4">
        <EmptyHeader className="items-center">
          <EmptyMedia variant="icon">
            <PackageOpenIcon />
          </EmptyMedia>
          <EmptyTitle className="text-xs font-normal text-grey-400">{children}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    </ComboboxPrimitive.Empty>
  );
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

function ComboboxChips({
  className,
  size = 'md',
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props & {
    /** Field height — `md` = 28px, `lg` = 32px. Grows when chips wrap. */
    size?: ComboboxSize;
  }) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      data-size={size}
      className={cn(
        TRIGGER_BASE_CLASS,
        // Empty = exact Select/Input height. With chips: unlock height, add py so wrapped
        // rows clear the border, and size chip height so a single row still hits the floor.
        SIZE_CHIPS_HEIGHT[size],
        SIZE_CHIP_ITEM[size],
        // relative + pr-7: the toggle chevron is pinned (absolute) right-centre, so it never wraps with
        // the chips; pr-7 reserves its lane so wrapped chips clear it.
        'relative box-border flex w-fit min-w-0 flex-wrap items-center gap-0.5 py-0 pr-7 pl-1 text-sm leading-none',
        'has-data-[slot=combobox-chip]:h-auto has-data-[slot=combobox-chip]:py-px has-data-[slot=combobox-chip]:pl-px',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        'flex h-6 w-fit max-w-full items-center justify-center gap-0.5 rounded-sm border border-grey-75 bg-grey-0 px-1.5 text-sm font-medium whitespace-nowrap text-grey-800 has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0.5',
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant="tertiary" size="sm" iconOnly className="size-4 opacity-50 hover:opacity-100" />}
          data-slot="combobox-chip-remove"
        >
          <XIcon className="pointer-events-none size-3" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
}

/** The "+N more" chip shown when a chips trigger is capped via `maxPills`; hover lists every selection. */
function ComboboxOverflowChip({ count, labels }: { count: number; labels: string[] }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-slot="combobox-overflow-chip"
          className="flex h-6 w-fit shrink-0 items-center rounded-sm border border-grey-75 bg-grey-0 px-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground"
        >
          +{count} more
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{labels.join(', ')}</TooltipContent>
    </Tooltip>
  );
}

function ComboboxChipsInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn(
        // Match chip height so the field stays on the size floor — bare <input> otherwise inflates.
        'h-6 min-w-12 flex-1 bg-transparent text-sm leading-none font-medium text-grey-800 outline-none placeholder:font-normal placeholder:text-grey-300 disabled:cursor-not-allowed disabled:text-grey-400',
        className,
      )}
      {...props}
    />
  );
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

interface ComboboxFieldBaseProps {
  /** Flat options and/or titled groups (see {@link ComboboxItems}). */
  options: ComboboxItems;
  /** DOM id on the trigger/input, so an external `<Label htmlFor>` associates with the field. */
  id?: string;
  placeholder?: string;
  'aria-label': string;
  /** Marks the field invalid — red border / glow matching Input / Select. */
  'aria-invalid'?: boolean;
  /** Ids of the description/error, so they are announced — `FormControl` passes them. */
  'aria-describedby'?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Shown when nothing matches. Unused with `allowCustomValue`, whose popup hides instead. */
  emptyMessage?: string;
  className?: string;
  /** Field height — `md` = 28px, `lg` = 32px */
  size?: ComboboxSize;
  /** `input` (default) = search in the trigger; `popup` = button trigger, search in the popover. */
  variant?: ComboboxVariant;
  /**
   * Portal the popup into this node (e.g. a Sheet/Dialog content ref). Required for clickable
   * options inside Radix modal layers that set `pointer-events: none` on `document.body`.
   */
  portalContainer?: React.ComponentProps<typeof ComboboxPrimitive.Portal>['container'];
}

type ComboboxFieldSingleProps = ComboboxFieldBaseProps & {
  mode?: 'single';
  value: string;
  onValueChange: (value: string) => void;
} & (
    | {
        allowCustomValue?: false;
        onBlur?: never;
      }
    | {
        /**
         * Free text (input variant only): the options are suggestions, and whatever is typed is the
         * value. `onValueChange` receives every keystroke, and an option's `value` when one is picked,
         * so an option's `label` should be its `value`. Enter keeps the typed text — nothing is
         * auto-highlighted for it to pick instead.
         */
        allowCustomValue: true;
        variant?: 'input';
        /** The input lost focus — the moment a free-text caller commits what was typed. */
        onBlur?: () => void;
      }
  );

type ComboboxFieldMultipleProps = ComboboxFieldBaseProps & {
  mode: 'multiple';
  value: string[];
  onValueChange: (value: string[]) => void;
} & (
    | {
        variant: 'popup';
        /** Render a custom trigger label from the selected options (e.g. "Events (3)"). */
        renderValue?: (selected: ComboboxOption[]) => React.ReactNode;
        maxPills?: never;
      }
    // The chips (input) trigger already reflects the selection, so renderValue has no effect there.
    | {
        variant?: 'input';
        renderValue?: never;
        /** Cap the chips shown in the trigger; the rest collapse into a "+N more" chip that lists every
         *  selected value on hover. Undefined (the default) shows every chip. */
        maxPills?: number;
      }
  );

export type ComboboxFieldProps = ComboboxFieldSingleProps | ComboboxFieldMultipleProps;

function optionEquals(a: ComboboxOption, b: ComboboxOption): boolean {
  return a.value === b.value;
}

/**
 * Wrap an option's row content in a tooltip. `pointer-events-auto` keeps it hoverable even when the
 * enclosing ComboboxItem is disabled (base-ui sets pointer-events:none on disabled items).
 */
function OptionTooltip({
  tooltip,
  children,
}: {
  tooltip: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="pointer-events-auto flex min-w-0 flex-1 items-center gap-2">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function renderSingleOption(item: ComboboxOption): React.ReactElement {
  const label = (
    <span className="flex min-w-0 flex-1 items-center gap-2 text-left whitespace-nowrap">
      {item.icon}
      <span className="truncate">{item.label}</span>
    </span>
  );
  return (
    <ComboboxItem key={item.value} value={item} title={item.label} disabled={item.disabled}>
      {item.tooltip ? <OptionTooltip tooltip={item.tooltip}>{label}</OptionTooltip> : label}
      {item.meta ? <span className="ml-2 shrink-0 text-xs">{item.meta}</span> : null}
    </ComboboxItem>
  );
}

/** Multi-select option row with a checkbox reflecting `selectedValues`. */
function renderMultiOption(item: ComboboxOption, selectedValues: string[]): React.ReactElement {
  const checked = selectedValues.includes(item.value);
  const content = (
    <>
      <Checkbox checked={checked} aria-hidden tabIndex={-1} className="pointer-events-none shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left whitespace-nowrap">{item.label}</span>
    </>
  );
  return (
    <ComboboxItem
      key={item.value}
      value={item}
      title={item.label}
      disabled={item.disabled}
      className="pr-2 **:data-[slot=combobox-item-indicator]:hidden"
    >
      {item.tooltip ? <OptionTooltip tooltip={item.tooltip}>{content}</OptionTooltip> : content}
    </ComboboxItem>
  );
}

function ComboboxPopupSearch({ disabled }: { disabled?: boolean }): React.ReactElement {
  return (
    <div className="border-b border-grey-100 p-1">
      <ComboboxInput showTrigger={false} placeholder="Search" disabled={disabled} />
    </div>
  );
}

function ComboboxPopupTrigger({
  id,
  size,
  loading,
  disabled,
  className,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  children,
}: {
  id?: string;
  size: ComboboxSize;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label': string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const isDisabled = [disabled, loading].some(Boolean);

  return (
    <ComboboxTrigger
      id={id}
      disabled={isDisabled}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid ? true : undefined}
      aria-describedby={ariaDescribedBy}
      aria-busy={loading ? true : undefined}
      className={cn(
        POPUP_TRIGGER_CLASS,
        SIZE_HEIGHT[size],
        loading && '**:data-[slot=combobox-trigger-icon]:hidden',
        className,
      )}
    >
      <span className="min-w-0 flex-1 truncate text-left">{children}</span>
      {loading && (
        <LoaderCircleIcon
          className="pointer-events-none size-4 shrink-0 animate-spin text-grey-500"
          aria-hidden="true"
        />
      )}
    </ComboboxTrigger>
  );
}

/** App-facing combobox: official Base UI Combobox with our tokens and string value API. */
export function ComboboxField(props: ComboboxFieldProps): React.ReactElement {
  if (props.mode === 'multiple') {
    return <ComboboxFieldMultiple {...props} />;
  }
  return <ComboboxFieldSingle {...props} />;
}

function ComboboxFieldSingle({
  options,
  id,
  value,
  onValueChange,
  placeholder,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  disabled = false,
  loading = false,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  className,
  size = 'md',
  variant = 'input',
  portalContainer,
  allowCustomValue = false,
  onBlur,
}: ComboboxFieldSingleProps): React.ReactElement {
  const { baseItems, grouped } = React.useMemo(() => toBaseUIItems(options), [options]);
  const flat = React.useMemo(() => flattenComboboxOptions(options), [options]);
  const filter = React.useMemo(() => keywordsFilter(flat), [flat]);
  const selected = flat.find((o) => o.value === value) ?? null;
  const isDisabled = disabled || loading;

  if (allowCustomValue) {
    // The input text IS the value: it is controlled from `value`, and only the user's own typing feeds
    // back. A picked option arrives through `onValueChange` instead.
    return (
      <Combobox
        items={baseItems}
        value={selected}
        onValueChange={(next) => {
          if (next) onValueChange(next.value);
        }}
        inputValue={value}
        onInputValueChange={(text, details) => {
          // Only the user's typing: Base UI also clears an unmatched input on Enter and on focus-out
          // (`input-clear`), which is exactly the text this mode exists to keep.
          if (details.reason === 'input-change') onValueChange(text);
        }}
        isItemEqualToValue={optionEquals}
        filter={filter}
        disabled={isDisabled}
      >
        <ComboboxInput
          id={id}
          placeholder={placeholder ?? ariaLabel}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid ? true : undefined}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          loading={loading}
          size={size}
          className={className}
          onBlur={onBlur}
        />
        {/* No match is not an empty result here, it is the admin's own value: the popup hides rather
            than cover the next field with a "nothing found" message. */}
        <ComboboxContent container={portalContainer} className="data-empty:hidden">
          <ComboboxList>{comboboxListChildren(grouped, renderSingleOption)}</ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }

  if (variant === 'popup') {
    return (
      <Combobox
        items={baseItems}
        value={selected}
        onValueChange={(next) => onValueChange(next?.value ?? '')}
        isItemEqualToValue={optionEquals}
        filter={filter}
        disabled={isDisabled}
        autoHighlight
      >
        <ComboboxPopupTrigger
          id={id}
          size={size}
          loading={loading}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={className}
        >
          <ComboboxValue>
            {(item: ComboboxOption | null) =>
              item ? (
                <span className="flex min-w-0 items-center gap-1.5">
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                  {item.meta ? (
                    <span className="flex items-center gap-1.5">
                      <span className="text-grey-300">·</span>
                      {item.meta}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="truncate font-normal text-grey-300">{placeholder ?? ariaLabel}</span>
              )
            }
          </ComboboxValue>
        </ComboboxPopupTrigger>
        <ComboboxContent className="flex flex-col" container={portalContainer}>
          <ComboboxPopupSearch disabled={isDisabled} />
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>{comboboxListChildren(grouped, renderSingleOption)}</ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }

  return (
    <Combobox
      items={baseItems}
      value={selected}
      onValueChange={(next) => onValueChange(next?.value ?? '')}
      isItemEqualToValue={optionEquals}
      filter={filter}
      disabled={isDisabled}
      autoHighlight
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder ?? ariaLabel}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid ? true : undefined}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        loading={loading}
        size={size}
        className={className}
      />
      <ComboboxContent container={portalContainer}>
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        <ComboboxList>{comboboxListChildren(grouped, renderSingleOption)}</ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

function ComboboxFieldMultiple({
  options,
  id,
  value,
  onValueChange,
  renderValue,
  maxPills,
  placeholder,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  disabled = false,
  loading = false,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  className,
  size = 'md',
  variant = 'input',
  portalContainer,
}: ComboboxFieldMultipleProps): React.ReactElement {
  const { baseItems, grouped } = React.useMemo(() => toBaseUIItems(options), [options]);
  const flat = React.useMemo(() => flattenComboboxOptions(options), [options]);
  const filter = React.useMemo(() => keywordsFilter(flat), [flat]);
  const selected = React.useMemo(
    () => value.map((v) => flat.find((o) => o.value === v)).filter((o): o is ComboboxOption => Boolean(o)),
    [flat, value],
  );
  const anchor = useComboboxAnchor();
  const isDisabled = disabled || loading;
  const handleValueChange = React.useCallback(
    (next: ComboboxOption[]) => {
      onValueChange(next.map((o) => o.value));
    },
    [onValueChange],
  );

  if (variant === 'popup') {
    return (
      <Combobox
        items={baseItems}
        multiple
        value={selected}
        onValueChange={handleValueChange}
        isItemEqualToValue={optionEquals}
        filter={filter}
        disabled={isDisabled}
        autoHighlight
      >
        <ComboboxPopupTrigger
          id={id}
          size={size}
          loading={loading}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={className}
        >
          {renderValue ? renderValue(selected) : <ComboboxValue placeholder={placeholder ?? ariaLabel} />}
        </ComboboxPopupTrigger>
        <ComboboxContent className="flex flex-col" container={portalContainer}>
          <ComboboxPopupSearch disabled={isDisabled} />
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>{comboboxListChildren(grouped, (item) => renderMultiOption(item, value))}</ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }

  return (
    <Combobox
      items={baseItems}
      multiple
      value={selected}
      onValueChange={handleValueChange}
      isItemEqualToValue={optionEquals}
      filter={filter}
      disabled={isDisabled}
      autoHighlight
    >
      <ComboboxChips
        ref={anchor}
        size={size}
        className={className}
        data-disabled={isDisabled || undefined}
        aria-invalid={ariaInvalid ? true : undefined}
      >
        <ComboboxValue>
          {(values: ComboboxOption[]) => {
            const shown = maxPills == null ? values : values.slice(0, maxPills);
            const extra = values.length - shown.length;
            return (
              <>
                {shown.map((item) => (
                  <ComboboxChip key={item.value}>{item.label}</ComboboxChip>
                ))}
                {extra > 0 && <ComboboxOverflowChip count={extra} labels={values.map((v) => v.label)} />}
              </>
            );
          }}
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          placeholder={selected.length === 0 ? (placeholder ?? ariaLabel) : undefined}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid ? true : undefined}
          aria-describedby={ariaDescribedBy}
          disabled={isDisabled}
          aria-busy={loading || undefined}
        />
        {/* Pinned right + centred across the whole (possibly multi-row) trigger, so it stays put when
            chips wrap instead of dropping to the last row / wrapping alone. */}
        <span className="absolute inset-y-0 right-2 flex items-center">
          {loading ? (
            <LoaderCircleIcon
              className="pointer-events-none size-4 shrink-0 animate-spin text-grey-500"
              aria-hidden="true"
            />
          ) : (
            <ComboboxTrigger className="shrink-0 text-grey-500" />
          )}
        </span>
      </ComboboxChips>
      <ComboboxContent anchor={anchor} container={portalContainer}>
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        <ComboboxList>{comboboxListChildren(grouped, (item) => renderMultiOption(item, value))}</ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export interface AsyncComboboxFieldProps {
  /** The options loaded so far — every page, in order. The server already filtered them. */
  options: readonly ComboboxOption[];
  value: string;
  /** Label for `value` when the current options do not contain it (another search, a later page). */
  selectedLabel?: string;
  onValueChange: (value: string) => void;
  /**
   * The user's own typing, unthrottled; the caller debounces and searches. Called with `''` when the
   * popup closes, so the next open starts from the unfiltered list.
   */
  onSearchChange: (text: string) => void;
  /** A search (or the first page) is in flight. */
  searching?: boolean;
  /**
   * The options answer an earlier query, not the text now in the input (typing still debouncing, or
   * the new search not back yet). They stay visible but cannot be highlighted or picked, so a quick
   * Enter never picks a list that does not match what was typed.
   */
  stale?: boolean;
  searchingMessage?: string;
  /**
   * Another page exists for the current search. It loads from the footer button, or from the
   * keyboard by moving the highlight onto the last option, or with ArrowDown / Enter when the page
   * has no options at all (Tab closes the popup, so a button is not reachable from the input). After
   * a failed search, Enter retries it the same way.
   */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  /** Announced to screen readers while the next page loads. */
  loadingMoreMessage?: string;
  /** The last search failed; shown in the popup with Retry instead of an empty list. */
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  disabled?: boolean;
  className?: string;
  size?: ComboboxSize;
  portalContainer?: React.ComponentProps<typeof ComboboxPrimitive.Portal>['container'];
}

// Keeps the input focused (and the popup open) when a footer control is pressed.
const keepInputFocus = (event: React.MouseEvent) => event.preventDefault();

/**
 * Single-select combobox whose options come from a server search: Base UI's `filter` is off, the
 * typed text goes to `onSearchChange`, and the popup carries the search's loading, error and
 * "load more" states. For a catalogue too large to load whole (Marketo's static lists).
 */
export function AsyncComboboxField({
  options,
  value,
  selectedLabel,
  onValueChange,
  onSearchChange,
  searching = false,
  stale = false,
  searchingMessage = 'Searching…',
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  loadMoreLabel = 'Load more',
  loadingMoreMessage = 'Loading more…',
  error = null,
  onRetry,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  id,
  placeholder,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  disabled = false,
  className,
  size = 'md',
  portalContainer,
}: AsyncComboboxFieldProps): React.ReactElement {
  const selected = React.useMemo<ComboboxOption | null>(() => {
    if (value.length === 0) return null;
    return options.find((o) => o.value === value) ?? { value, label: selectedLabel ?? value };
  }, [options, selectedLabel, value]);
  const showEmpty = !searching && error === null;
  const items = React.useMemo(
    () => (stale ? options.map((option) => ({ ...option, disabled: true })) : options),
    [options, stale],
  );
  const canLoadMore = hasMore && error === null && !stale && onLoadMore !== undefined;

  return (
    <Combobox
      items={items}
      value={selected}
      onValueChange={(next: ComboboxOption | null) => onValueChange(next?.value ?? '')}
      onItemHighlighted={(item: ComboboxOption | undefined, details: { reason: string; index: number }) => {
        // Arrowing onto the last option asks for the next page: the keyboard path to "load more".
        if (
          item !== undefined &&
          details.reason === 'keyboard' &&
          details.index === items.length - 1 &&
          canLoadMore &&
          !loadingMore
        ) {
          onLoadMore?.();
        }
      }}
      onInputValueChange={(text: string, details: { reason: string }) => {
        // Only the user's typing searches. Base UI also writes the picked label into the input
        // (`item-press`) and clears it on close; neither is a query.
        if (details.reason === 'input-change') onSearchChange(text);
      }}
      onOpenChange={(open: boolean) => {
        if (!open) onSearchChange('');
      }}
      isItemEqualToValue={optionEquals}
      filter={null}
      disabled={disabled}
      autoHighlight
    >
      <ComboboxInput
        id={id}
        onKeyDown={(event: BaseUIEvent<React.KeyboardEvent<HTMLInputElement>>) => {
          // Base UI closes the popup on Enter with nothing highlighted, which clears the search; these
          // keys are ours here, so its handler is skipped (preventDefault alone does not stop it).
          const take = (action: () => void) => {
            event.preventDefault();
            event.preventBaseUIHandler();
            action();
          };
          // A failed search: Enter retries the same query (and page), without leaving the input.
          if (error !== null && onRetry !== undefined && event.key === 'Enter') {
            take(onRetry);
            return;
          }
          // No option to arrow onto (an empty page with more behind it): the keys ask for the next page.
          if (
            items.length === 0 &&
            canLoadMore &&
            !loadingMore &&
            (event.key === 'ArrowDown' || event.key === 'Enter')
          ) {
            take(() => onLoadMore?.());
          }
        }}
        placeholder={placeholder ?? ariaLabel}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid ? true : undefined}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        size={size}
        className={className}
      />
      <ComboboxContent container={portalContainer} className="flex flex-col">
        {searching ? (
          <div role="status" className="flex items-center gap-2 px-3 pt-2.5 pb-1 text-xs text-muted-foreground">
            <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin" aria-hidden="true" />
            {searchingMessage}
          </div>
        ) : null}
        {error !== null ? (
          <div role="alert" className="flex items-center justify-between gap-3 px-3 py-2.5 text-xs text-danger">
            <span className="min-w-0">
              {error}
              {onRetry ? ' Press Enter to retry.' : null}
            </span>
            {onRetry ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                label="Retry"
                className="shrink-0"
                onMouseDown={keepInputFocus}
                onClick={onRetry}
              />
            ) : null}
          </div>
        ) : null}
        {showEmpty ? <ComboboxEmpty>{emptyMessage}</ComboboxEmpty> : null}
        <ComboboxList>{renderSingleOption}</ComboboxList>
        <span role="status" className="sr-only">
          {loadingMore ? loadingMoreMessage : ''}
        </span>
        {canLoadMore ? (
          <div className="border-t border-grey-100 p-1.5">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              className="w-full"
              label={loadingMore ? 'Loading…' : loadMoreLabel}
              loading={loadingMore}
              onMouseDown={keepInputFocus}
              onClick={onLoadMore}
            />
          </div>
        ) : null}
      </ComboboxContent>
    </Combobox>
  );
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
};
